import { CardImageConfig, Content } from '@/types/admin/who-we-are';
import { useState } from 'react';
import { CardContent } from '../../card-content/CardContent';
import './CardsSection.scss';
import { Button } from '@/components/admin/button/Button';
import { IconButton } from '@/components/admin/icon-button/IconButton';
import { getRichTextContentFromHtml } from '@/utils/functions/rich-text-content/rich-text-content';
import { WHO_WE_ARE_VALIDATION_FUNCTIONS } from '@/validation/admin/who-we-are-schema/WhoWeAreSchema';
import { ContentType } from '@/types/common/about-us';
import { LocalizationLanguage } from '@/types/common/language';
import { COMMON_TEXT_ADMIN } from '@/const/admin/common';
import { DEFAULT_LOCALE } from '@/const/common/locales';
import { ACTION_ICONS } from '@/const/common/action-icons';

export interface CardsSectionProps {
    content: Content[] | undefined;
    descriptionLimit: number;
    rows?: number;
    onChange: (data: Content) => void;
    cardImageConfigs: CardImageConfig[];
    titleText?: string;
    onPublish: () => void;
    onTranslate: () => void;
    isPublishButtonActive: boolean;
    language: LocalizationLanguage;
}

export const CardsSection = ({
    content,
    descriptionLimit,
    rows,
    onChange,
    onPublish,
    onTranslate,
    cardImageConfigs,
    titleText,
    isPublishButtonActive,
    language,
}: CardsSectionProps) => {
    const [errors, setErrors] = useState<Record<number, { image: string | null; description: string | null }>>({});

    if (!content) return null;

    const cardContents = content.filter((item) => item.contentType === ContentType.Card);
    const isBaseLanguage = language.code === DEFAULT_LOCALE;

    const handleDescriptionError = (id: number, error: string | null) => {
        setErrors((prev) => {
            const cardErrors = prev[id] || { image: null, description: null };
            return {
                ...prev,
                [id]: {
                    ...cardErrors,
                    description: error,
                },
            };
        });
    };

    const handleSetImageError = (id: number, value: string | null) => {
        setErrors((prev) => {
            const cardErrors = prev[id] || { image: null, description: null };
            return {
                ...prev,
                [id]: {
                    ...cardErrors,
                    image: value,
                },
            };
        });
    };

    if (cardContents.length === 0) {
        return null;
    }

    const handlePublish = () => {
        const updatedErrors = { ...errors };

        cardContents.forEach((card) => {
            const descriptionError = WHO_WE_ARE_VALIDATION_FUNCTIONS.validateText(
                getRichTextContentFromHtml(card.description ?? ''),
            );

            updatedErrors[card.id] = {
                image: errors[card.id]?.image ?? null,
                description: descriptionError || null,
            };
        });

        setErrors(updatedErrors);

        const hasErrors = cardContents.some((card) => {
            const error = updatedErrors[card.id];
            return error?.description != null || error?.image != null;
        });

        if (hasErrors || !isPublishButtonActive || !isBaseLanguage) return;

        onPublish();
    };

    const isPublishDisabled =
        cardContents.some((card) =>
            Boolean(WHO_WE_ARE_VALIDATION_FUNCTIONS.validateText(getRichTextContentFromHtml(card.description ?? ''))),
        ) ||
        cardContents.some((card) => errors[card.id]?.image != null) ||
        !isPublishButtonActive ||
        !isBaseLanguage;

    return (
        <>
            <div className="cards-section-wrapper">
                {titleText && <span className="cards-section-wrapper-title">{titleText}</span>}
                <div className="cards-section-wrapper-buttons">
                    <IconButton
                        type="button"
                        className="translate-btn"
                        aria-label="Translate"
                        onClick={onTranslate}
                        DefaultIcon={ACTION_ICONS.translate.default}
                        disabled={isPublishButtonActive}
                    />
                </div>
                <div className="cards-section-wrapper-cards">
                    {cardContents.map((c: Content, index: number) => (
                        <CardContent
                            key={c.id}
                            content={c}
                            onChange={onChange}
                            onDescriptionValidate={(error) => handleDescriptionError(c.id, error)}
                            imageError={errors[c.id]?.image ?? null}
                            setImageError={(value) => handleSetImageError(c.id, value)}
                            descriptionLimit={descriptionLimit}
                            imageInputProps={{ ...(cardImageConfigs[index] || {}) }}
                            rows={rows}
                            language={language}
                        />
                    ))}
                </div>
            </div>
            <div className="button-section">
                {isBaseLanguage && (
                    <Button
                        className="button"
                        buttonStyle="primary"
                        onClick={handlePublish}
                        type="submit"
                        disabled={isPublishDisabled}
                    >
                        {COMMON_TEXT_ADMIN.BUTTON.SAVE_AS_PUBLISHED}
                    </Button>
                )}
            </div>
        </>
    );
};
